import { useState, useEffect, useCallback } from 'react';
import { PROGRESSION_CRITERIA } from '../utils/progressionCriteria';
import { supabase } from '../lib/supabase';

/**
 * Hook to check user's progression toward next skill level
 * 
 * @param {Object} userSettings - User settings object
 * @param {Object} progress - Koch progress object
 * @returns {Object} Progression status and functions
 */
export const useProgressionChecker = (userSettings, progress) => {
    const [progressionStatus, setProgressionStatus] = useState(null);
    const [isChecking, setIsChecking] = useState(false);

    const checkProgression = useCallback(async () => {
        const currentLevel = userSettings?.skill_level || 1;

        // Max level reached
        if (currentLevel >= 4) {
            setProgressionStatus({
                canAdvance: false,
                atMaxLevel: true,
                currentLevel: 4
            });
            return null;
        }

        setIsChecking(true);

        try {
            // Fetch recent activities from database
            const { data: user } = await supabase.auth.getUser();
            let activities = [];

            if (user?.user) {
                const { data, error } = await supabase
                    .from('activities')
                    .select('*')
                    .eq('user_id', user.user.id)
                    .order('created_at', { ascending: false })
                    .limit(100); // Last 100 sessions

                if (!error && data) {
                    activities = data;
                }
            }

            const criteria = PROGRESSION_CRITERIA[currentLevel];
            if (!criteria) {
                setProgressionStatus(null);
                setIsChecking(false);
                return null;
            }

            // Check primary path (Level 1 only - Koch completion)
            if (criteria.primaryPath) {
                const primaryMet = criteria.primaryPath.check(userSettings, activities, progress);

                if (primaryMet) {
                    const status = {
                        canAdvance: true,
                        currentLevel,
                        nextLevel: currentLevel + 1,
                        criteria: criteria.name,
                        description: criteria.description,
                        method: 'primary',
                        methodDescription: criteria.primaryPath.description,
                        overallProgress: 100
                    };
                    setProgressionStatus(status);
                    setIsChecking(false);
                    return status;
                }
            }

            // Check alternative path or requirements
            const requirements = criteria.alternativePath?.requirements || criteria.requirements || [];
            const results = requirements.map(req => {
                const result = req.check(userSettings, activities, progress);
                return {
                    ...req,
                    ...result
                };
            });

            const allMet = results.every(r => r.met);
            const overallProgress = results.length > 0
                ? Math.round(results.reduce((sum, r) => sum + (r.progress || 0), 0) / results.length)
                : 0;

            const status = {
                canAdvance: allMet,
                currentLevel,
                nextLevel: currentLevel + 1,
                criteria: criteria.name,
                description: criteria.description,
                requirements: results,
                overallProgress,
                method: criteria.alternativePath ? 'alternative' : 'standard'
            };

            setProgressionStatus(status);
            setIsChecking(false);
            return status;

        } catch (error) {
            console.error('Error checking progression:', error);
            setIsChecking(false);
            return null;
        }
    }, [userSettings, progress]);

    // Check progression when userSettings or progress changes
    useEffect(() => {
        if (userSettings) {
            checkProgression();
        }
    }, [userSettings?.skill_level, progress?.maxLevel, checkProgression]);

    return {
        progressionStatus,
        isChecking,
        checkProgression,
        canAdvance: progressionStatus?.canAdvance || false,
        overallProgress: progressionStatus?.overallProgress || 0
    };
};
